import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { PulseLoader } from "react-spinners";
import useModuleAccess from "../hooks/useModuleAccess";

const API_URL = process.env.REACT_APP_API_URL;

const SALES_ORDER_STAT_CARDS = [
  {
    key: "totalCount",
    label: "Total Orders",
    titleColor: "#127aea",
    valueColor: "#2c7fd8",
    filterValue: null,
    filterType: null,
  },
  {
    key: "activeCount",
    label: "Active",
    titleColor: "#196F3D",
    valueColor: "#27AE60",
    filterValue: "Active",
    filterType: "status",
  },
  {
    key: "poCreatedCount",
    label: "Po created",
    titleColor: "#6C3483",
    valueColor: "#8E44AD",
    filterValue: "Po created",
    filterType: "cpoStatus",
  },
  {
    key: "poPartiallyCreatedCount",
    label: "Po Partially created",
    titleColor: "#B45309",
    valueColor: "#D97706",
    filterValue: "Po Partially created",
    filterType: "cpoStatus",
  },
   {
    key: "goPartiallyCreatedCount",
    label: "GO Partially Created",
    titleColor: "#be0c71",
    valueColor: "#f3329f",
    filterValue: "GO PARTIALLY CREATED",
    filterType: "goStatus",
  },
  {
    key: "goCreatedCount",
    label: "GO Created",
    titleColor: "#0E6655",
    valueColor: "#16A085",
    filterValue: "GO CREATED",
    filterType: "goStatus",
  },
  {
    key: "draftCount",
    label: "Draft",
    titleColor: "#5D6D7E",
    valueColor: "#7F8C8D",
    filterValue: "Draft",
    filterType: "status",
  },
  {
    key: "grossTotalValue",
    label: "Gross Total",
    titleColor: "#1A5276",
    valueColor: "#2874A6",
    filterValue: null,
    filterType: null,
    format: "currency",
    clickable: false,
  },
];

const CPO_STATUS_OPTIONS = [
  "Po Partially created",
  "Po created",
  "Active",
];

const STATUS_FILTER_OPTIONS = SALES_ORDER_STAT_CARDS.filter(
  (card) => card.filterType === "status" && card.filterValue,
).map((card) => ({ value: card.filterValue, label: card.label }));

const DEFAULT_SORT_BY = "createdAt";
const DEFAULT_SORT_ORDER = "desc";

const SORT_COLUMN_MAP = {
  cpoNumber: "cpoNumber",
  cpoDate: "cpoDate",
  deliverDate: "deliverDate",
  customerName: "customerName",
  status: "status",
  cpoStatus: "cpoStatus",
  goStatus: "goStatus",
  createdBy: "createdBy",
  createdAt: "createdAt",
  updatedBy: "updatedBy",
};

const DEFAULT_FILTER_CHECKS = {
  status: false,
  cpoStatus: false,
  goStatus: false,
  customer: false,
  createdBy: false,
  dateRange: false,
};
const DEFAULT_FILTER_INPUTS = {
  status: "",
  cpoStatus: "",
  customer: "",
  createdBy: "",
  goStatus: "",
  fromDate: "",
  toDate: "",
};
const DEFAULT_ACTIVE_MULTI_FILTERS = {
  status: "",
  cpoStatus: "",
  customer: "",
  createdBy: "",
  goStatus: "",
  fromDate: "",
  toDate: "",
};

const MAX_ACTIVE_FILTERS = 2;

const SalesOrderStatCard = ({
  label,
  value,
  fullValue,
  titleColor,
  valueColor,
  onClick,
  isActive,
  clickable = true,
}) => (
  <div className="sales-order-stat-col">
    <div
      className={`card supplier-stat-card${
        clickable ? " procurement-stat-card-clickable" : ""
      }${isActive ? " procurement-stat-card-active" : ""}`}
      onClick={clickable ? onClick : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      title={fullValue ? fullValue : clickable ? `Filter by ${label}` : label}
      style={clickable ? undefined : { cursor: "default" }}
    >
      <div className="card-body">
        <div className="stat-title" style={{ color: titleColor }}>
          {label}
        </div>
        <div className="stat-value" style={{ color: valueColor }}>
          {value}
        </div>
      </div>
    </div>
  </div>
);

const SalesOrderStatCardSkeleton = () => (
  <div className="sales-order-stat-col">
    <div className="card supplier-stat-card">
      <div className="card-body">
        <div className="stat-skeleton stat-skeleton-title" />
        <div className="stat-skeleton stat-skeleton-value" />
      </div>
    </div>
  </div>
);

function parseApiResponse(raw) {
  let d = raw;
  if (typeof d === "string") {
    try {
      d = JSON.parse(d);
    } catch {
      return raw;
    }
  }
  if (d?.body != null) {
    if (typeof d.body === "string") {
      try {
        return JSON.parse(d.body);
      } catch {
        return d.body;
      }
    }
    return d.body;
  }
  return d;
}

function parseJsonIfString(value) {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function normalizeApiPayload(raw) {
  let obj = raw;
  if (!obj || typeof obj !== "object") return raw;
  obj = { ...obj };
  if (typeof obj.data === "string") {
    obj.data = parseJsonIfString(obj.data);
  }
  if (
    obj.data &&
    typeof obj.data === "object" &&
    typeof obj.data.data === "string"
  ) {
    obj.data = { ...obj.data, ...parseJsonIfString(obj.data.data) };
  }
  return obj;
}

function isApiSuccess(responseData) {
  if (!responseData || typeof responseData !== "object") return false;
  const s = responseData.success;
  return s === true || s === "true" || s === 1 || s === "1";
}

function extractSalesOrdersPayload(responseData) {
  const root = responseData;
  let dataBlock = root?.data != null ? root.data : root;

  if (typeof dataBlock === "string") {
    dataBlock = parseJsonIfString(dataBlock);
  }
  if (!dataBlock || typeof dataBlock !== "object") {
    return { salesOrders: [], pagination: {} };
  }

  if (Array.isArray(dataBlock)) {
    return {
      salesOrders: dataBlock,
      pagination:
        root.pagination && typeof root.pagination === "object"
          ? root.pagination
          : {},
    };
  }

  let list =
    dataBlock.salesOrders ||
    dataBlock.orders ||
    dataBlock.items ||
    root.salesOrders;

  if (list == null && dataBlock.data != null) {
    const inner =
      typeof dataBlock.data === "string"
        ? parseJsonIfString(dataBlock.data)
        : dataBlock.data;
    if (inner && typeof inner === "object") {
      list = inner.salesOrders || inner.orders || inner.items;
    }
  }

  if (
    (list == null || (Array.isArray(list) && list.length === 0)) &&
    dataBlock.salesOrders == null &&
    (dataBlock._id != null ||
      dataBlock.id != null ||
      dataBlock.cpoNumber != null)
  ) {
    list = [dataBlock];
  }

  const pagination =
    (dataBlock && dataBlock.pagination) || root.pagination || {};

  if (typeof list === "string") {
    list = parseJsonIfString(list);
  }

  const total =
    typeof pagination.total === "number"
      ? pagination.total
      : typeof pagination.totalCount === "number"
        ? pagination.totalCount
        : typeof dataBlock.totalCount === "number"
          ? dataBlock.totalCount
          : typeof dataBlock.total === "number"
            ? dataBlock.total
            : undefined;

  const currentPage =
    typeof dataBlock.currentPage === "number"
      ? dataBlock.currentPage
      : typeof pagination.currentPage === "number"
        ? pagination.currentPage
        : undefined;

  const limit =
    typeof dataBlock.limit === "number"
      ? dataBlock.limit
      : typeof pagination.limit === "number"
        ? pagination.limit
        : undefined;

  const totalPages =
    typeof dataBlock.totalPages === "number"
      ? dataBlock.totalPages
      : typeof pagination.totalPages === "number"
        ? pagination.totalPages
        : undefined;

  return {
    salesOrders: Array.isArray(list) ? list : [],
    pagination: {
      ...(pagination &&
      typeof pagination === "object" &&
      !Array.isArray(pagination)
        ? pagination
        : {}),
      ...(total != null ? { total, totalCount: total } : {}),
      ...(currentPage != null ? { currentPage } : {}),
      ...(limit != null ? { limit } : {}),
      ...(totalPages != null ? { totalPages } : {}),
      ...(dataBlock.sortBy ? { sortBy: dataBlock.sortBy } : {}),
      ...(dataBlock.sortOrder ? { sortOrder: dataBlock.sortOrder } : {}),
    },
  };
}

function formatOrderDate(value) {
  if (!value) return "-";
  const str = String(value).trim();
  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) return str;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return str || "-";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "Asia/Kolkata",
  });
}

function formatAmount(value) {
  if (value == null || value === "") return "-";
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";
  return num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatAmountCompact(value) {
  if (value == null || value === "") return "-";
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";
  const abs = Math.abs(num);
  if (abs >= 10000000) return `₹ ${(num / 10000000).toFixed(2)} Cr`;
  if (abs >= 100000) return `₹ ${(num / 100000).toFixed(2)} L`;
  return `₹ ${formatAmount(num)}`;
}

function getCurrencySymbol(currency) {
  const value = String(currency || "")
    .trim()
    .toLowerCase();
  if (value === "dollar" || value === "usd" || value === "$") return "$";
  if (value === "euro" || value === "eur" || value === "€") return "€";
  return "₹";
}

function getOrderId(order) {
  return order?._id || order?.id || "";
}

function cpoNumberValue(order) {
  return order?.cpoNumber || order?.cpoNo || "-";
}

function customerNameValue(order) {
  return String(order?.customerName || "").trim() || "-";
}

function summaryValue(order) {
  const s = order?.cpoSummary || order?.summary || "";
  return s.trim() ? s : "-";
}

function createdByName(order) {
  const createdBy = order?.createdBy;
  if (!createdBy) return "-";
  if (typeof createdBy === "string") return createdBy.trim() || "-";
  return createdBy.name || createdBy.userName || "-";
}

function capitalizeStatus(value) {
  if (!value) return "-";
  const s = String(value).trim();
  if (!s) return "-";
  return s
    .split(",")
    .map((part) => {
      const trimmed = part.trim();
      if (!trimmed) return trimmed;
      return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
    })
    .join(", ");
}

function renderRecordStatus(status) {
  const normalized = String(status || "").toLowerCase();
  const label = capitalizeStatus(status);
  let badge = "badge-info";
  if (normalized === "active") badge = "badge-success";
  if (normalized === "draft") badge = "badge-secondary";
  return <span className={`badge ${badge}`}>{label}</span>;
}

function renderStandalone(order) {
  const value = order?.standalone;
  const isStandalone =
    value === true ||
    value === "true" ||
    value === "yes" ||
    value === 1 ||
    value === "1";
  return isStandalone ? "Yes" : "No";
}

function renderGrossTotal(order) {
  const amount = formatAmount(order?.grossTotalValue);
  if (amount === "-") return "-";
  return `${getCurrencySymbol(order?.currency)} ${amount}`;
}

function extractCustomersList(payload) {
  const data = payload?.data != null ? payload.data : payload;
  const list =
    (Array.isArray(data?.customers) && data.customers) ||
    (Array.isArray(data?.data) && data.data) ||
    (Array.isArray(payload?.customers) && payload.customers) ||
    (Array.isArray(data) && data) ||
    [];
  return list
    .map((item) => ({
      _id: String(item?._id || item?.id || "").trim(),
      customerName: String(item?.customerName || item?.name || "").trim(),
    }))
    .filter((item) => item._id && item.customerName);
}

function normalizeUsers(list) {
  if (!Array.isArray(list)) return [];
  return list
    .map((user) => ({
      _id: String(user?._id || user?.id || "").trim(),
      name: String(user?.name || user?.userName || user?.email || "").trim(),
    }))
    .filter((user) => user._id && user.name);
}

function formatStatCardValue(card, value) {
  if (card.format === "currency") {
    return {
      display: formatAmountCompact(value),
      full: value != null ? `₹ ${formatAmount(value)}` : undefined,
    };
  }
  if (value == null || value === "") {
    return { display: "0" };
  }
  return { display: String(value) };
}

const SalesOrder = () => {
  const { canCreate, canView, canUpdate, canDelete } =
    useModuleAccess("sales_order");

  const savedFilters = JSON.parse(
    sessionStorage.getItem("salesOrderFilters") || "{}",
  );

  const GO_STATUS_OPTIONS = [
  "GO INPROGRESS",
  "GO PARTIALLY CREATED",
  "GO CREATED",
];

const GO_STATUS_LABELS = {
  "GO INPROGRESS": "Go In Progress",
  "GO PARTIALLY CREATED": "Go Partially Created",
  "GO CREATED": "Go Created",
  "GO NOT CREATED": "Go Not Created",
};

function formatGoStatusLabel(value) {
  if (!value) return "-";
  const key = String(value).trim().toUpperCase();
  return GO_STATUS_LABELS[key] || capitalizeStatus(value);
}

  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(
    savedFilters.searchQuery || "",
  );
  const [activeSearchQuery, setActiveSearchQuery] = useState(
    savedFilters.activeSearchQuery || "",
  );
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(savedFilters.currentPage || 1);
  const [itemsPerPage, setItemsPerPage] = useState(
    savedFilters.itemsPerPage || 25,
  );
  const [deleteOrderId, setDeleteOrderId] = useState(null);
  const [deleteCpoNumber, setDeleteCpoNumber] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [filterChecks, setFilterChecks] = useState({
    ...DEFAULT_FILTER_CHECKS,
    ...(savedFilters.filterChecks || {}),
  });
  const [filterInputs, setFilterInputs] = useState({
    ...DEFAULT_FILTER_INPUTS,
    ...(savedFilters.filterInputs || {}),
  });
  const [activeMultiFilters, setActiveMultiFilters] = useState({
    ...DEFAULT_ACTIVE_MULTI_FILTERS,
    ...(savedFilters.activeMultiFilters || {}),
  });
  const filterPanelRef = useRef(null);

  const [orderUsers, setOrderUsers] = useState([]);
  const [isOrderUsersLoading, setIsOrderUsersLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [isCustomersLoading, setIsCustomersLoading] = useState(false);

  const [sortBy, setSortBy] = useState(savedFilters.sortBy || DEFAULT_SORT_BY);
  const [sortOrder, setSortOrder] = useState(
    savedFilters.sortOrder || DEFAULT_SORT_ORDER,
  );

  const [isStatusLoading, setIsStatusLoading] = useState(true);
  const [orderStatus, setOrderStatus] = useState({
    totalCount: 0,
    draftCount: 0,
    activeCount: 0,
    poPartiallyCreatedCount: 0,
    poCreatedCount: 0,
    goPartiallyCreatedCount: 0, 
    goCreatedCount: 0,
    grossTotalValue: 0,
  });

  const anyMultiFilterActive =
    !!activeMultiFilters.status ||
    !!activeMultiFilters.cpoStatus ||
    !!activeMultiFilters.goStatus ||
    !!activeMultiFilters.customer ||
    !!activeMultiFilters.createdBy ||
    !!activeMultiFilters.fromDate ||
    !!activeMultiFilters.toDate;

  const getCurrentUserId = () => localStorage.getItem("id") || undefined;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        filterPanelRef.current &&
        !filterPanelRef.current.contains(e.target)
      ) {
        setIsFilterPanelOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);


  function toApiDDMMYYYY(isoDate) {
    if (!isoDate) return "";
    const [yyyy, mm, dd] = isoDate.split("-");
    if (!yyyy || !mm || !dd) return "";
    return `${dd}-${mm}-${yyyy}`;
  }

  const buildMultiFilterFields = useCallback(
    (fields = {}) => {
      if (activeMultiFilters.status) fields.status = activeMultiFilters.status;
      if (activeMultiFilters.cpoStatus) {
        fields.cpoStatus = activeMultiFilters.cpoStatus;
      }
      if (activeMultiFilters.goStatus) {
        fields.goStatus = activeMultiFilters.goStatus;
      }
      if (activeMultiFilters.createdBy) {
        fields.user = activeMultiFilters.createdBy;
        fields.createdBy = activeMultiFilters.createdBy;
      }
      if (activeMultiFilters.customer) {
        fields.customer = activeMultiFilters.customer;
        fields.customerId = activeMultiFilters.customer;
      }
      if (activeMultiFilters.fromDate) {
        fields.fromDate = toApiDDMMYYYY(activeMultiFilters.fromDate);
      }
      if (activeMultiFilters.toDate) {
        fields.toDate = toApiDDMMYYYY(activeMultiFilters.toDate);
      }
      return fields;
    },
    [activeMultiFilters],
  );

  const fetchOrders = useCallback(async () => {
    setIsLoading(true);
    try {
      const payload = {
        action: "get-all-sales-orders",
        page: currentPage,
        limit: itemsPerPage,
        currentUserId: getCurrentUserId(),
      };

      const trimmedSearch = activeSearchQuery ? activeSearchQuery.trim() : "";
      if (trimmedSearch) {
        payload.search = trimmedSearch;
      }

      buildMultiFilterFields(payload);

      if (sortBy) {
        payload.sortBy = sortBy;
        payload.sortOrder = sortOrder;
      }

      const response = await axios.post(API_URL, payload);
      const responseData = normalizeApiPayload(parseApiResponse(response.data));
      const { salesOrders: list, pagination } =
        extractSalesOrdersPayload(responseData);

      const ok =
        isApiSuccess(responseData) || (Array.isArray(list) && list.length > 0);

      if (ok) {
        setOrders(list);
        const total =
          typeof pagination.total === "number"
            ? pagination.total
            : typeof pagination.totalCount === "number"
              ? pagination.totalCount
              : list.length;
        setTotalCount(total);
      } else {
        toast.error(responseData?.message || "Failed to fetch sales orders", {
          autoClose: 2000,
        });
        setOrders([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Fetch sales orders error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      toast.error(
        errData?.message || "An error occurred while fetching sales orders",
        { autoClose: 2000 },
      );
      setOrders([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [
    currentPage,
    itemsPerPage,
    activeSearchQuery,
    sortBy,
    sortOrder,
    buildMultiFilterFields,
  ]);

  const fetchOrderStatus = useCallback(async () => {
    setIsStatusLoading(true);
    try {
      const payload = {
        action: "sales-order-count",
        currentUserId: getCurrentUserId(),
      };

      const trimmedSearch = activeSearchQuery ? activeSearchQuery.trim() : "";
      if (trimmedSearch) {
        payload.search = trimmedSearch;
      }

      if (activeMultiFilters.customer) {
        payload.customerId = activeMultiFilters.customer;
      }
      if (activeMultiFilters.createdBy) {
        payload.createdBy = activeMultiFilters.createdBy;
      }
      if (activeMultiFilters.cpoStatus) {
        payload.cpoStatus = activeMultiFilters.cpoStatus;
      }
       if (activeMultiFilters.goStatus) {
        payload.goStatus = activeMultiFilters.goStatus;
      }
      if (activeMultiFilters.status) {
        payload.status = activeMultiFilters.status;
      }
      if (activeMultiFilters.fromDate) {
        payload.fromDate = toApiDDMMYYYY(activeMultiFilters.fromDate);
      }
      if (activeMultiFilters.toDate) {
        payload.toDate = toApiDDMMYYYY(activeMultiFilters.toDate);
      }

      const response = await axios.post(API_URL, payload);
      const responseData = normalizeApiPayload(parseApiResponse(response.data));
      const data = responseData?.data || responseData || {};

      if (isApiSuccess(responseData) || data.totalCount != null) {
        setOrderStatus({
          totalCount: Number(data.totalCount) || 0,
          draftCount: Number(data.draftCount) || 0,
          activeCount: Number(data.activeCount) || 0,
          poPartiallyCreatedCount:
            Number(
              data.poPartiallyCreatedCount ?? data.poPartiallyCreated,
            ) || 0,
          poCreatedCount: Number(data.poCreatedCount ?? data.poCreated) || 0,
           goPartiallyCreatedCount: Number(data.goPartiallyCreatedCount) || 0, // NEW
          goCreatedCount: Number(data.goCreatedCount) || 0,
          grossTotalValue: Number(data.grossTotalValue) || 0,
        });
      } else {
        setOrderStatus((prev) => ({
          ...prev,
          totalCount,
        }));
      }
    } catch (error) {
      console.error("Fetch sales order count error:", error);
      setOrderStatus((prev) => ({
        ...prev,
        totalCount,
      }));
    } finally {
      setIsStatusLoading(false);
    }
  }, [activeSearchQuery, activeMultiFilters, totalCount]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    fetchOrderStatus();
  }, [fetchOrderStatus]);

  useEffect(() => {
    let cancelled = false;

    const fetchUsers = async () => {
      setIsOrderUsersLoading(true);
      try {
        const response = await axios.post(API_URL, {
          action: "get-sales-order-users",
          currentUserId: getCurrentUserId(),
        });
        const responseData = normalizeApiPayload(
          parseApiResponse(response.data),
        );
        if (cancelled) return;
        const users = Array.isArray(responseData?.data)
          ? responseData.data
          : Array.isArray(responseData?.users)
            ? responseData.users
            : Array.isArray(responseData?.data?.users)
              ? responseData.data.users
              : [];
        if (isApiSuccess(responseData) || users.length > 0) {
          setOrderUsers(normalizeUsers(users));
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Fetch users error:", error);
        }
      } finally {
        if (!cancelled) setIsOrderUsersLoading(false);
      }
    };

    const fetchCustomers = async () => {
      setIsCustomersLoading(true);
      try {
        const response = await axios.post(API_URL, {
          action: "get-all-customers",
          search: "",
          currentUserId: getCurrentUserId(),
        });
        const responseData = normalizeApiPayload(
          parseApiResponse(response.data),
        );
        if (cancelled) return;
        if (isApiSuccess(responseData)) {
          setCustomers(extractCustomersList(responseData));
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Fetch customers error:", error);
        }
      } finally {
        if (!cancelled) setIsCustomersLoading(false);
      }
    };

    fetchUsers();
    fetchCustomers();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    sessionStorage.setItem(
      "salesOrderFilters",
      JSON.stringify({
        searchQuery,
        activeSearchQuery,
        currentPage,
        itemsPerPage,
        filterChecks,
        filterInputs,
        activeMultiFilters,
        sortBy,
        sortOrder,
      }),
    );
  }, [
    searchQuery,
    activeSearchQuery,
    currentPage,
    itemsPerPage,
    filterChecks,
    filterInputs,
    activeMultiFilters,
    sortBy,
    sortOrder,
  ]);

  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage) || 1);
  const startItem =
    orders.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const endItem =
    orders.length > 0 ? Math.min(currentPage * itemsPerPage, totalCount) : 0;

  const hasActiveFilters = Boolean(activeSearchQuery || anyMultiFilterActive);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSearchTrigger = () => {
    setActiveSearchQuery(searchQuery.trim());
    setCurrentPage(1);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearchTrigger();
    }
  };

  const handleFilterCheckToggle = (key) => {
    setFilterChecks((prev) => {
      const isCurrentlyChecked = prev[key];

      if (!isCurrentlyChecked) {
        const activeCount = Object.values(prev).filter(Boolean).length;
        if (activeCount >= MAX_ACTIVE_FILTERS) {
          toast.error(
            `You can select up to ${MAX_ACTIVE_FILTERS} filters only`,
            {
              autoClose: 2000,
            },
          );
          return prev;
        }
      }

      const next = { ...prev, [key]: !isCurrentlyChecked };

      if (!next[key]) {
        if (key === "dateRange") {
          setFilterInputs((inputs) => ({
            ...inputs,
            fromDate: "",
            toDate: "",
          }));
          setActiveMultiFilters((active) => ({
            ...active,
            fromDate: "",
            toDate: "",
          }));
        } else {
          setFilterInputs((inputs) => ({ ...inputs, [key]: "" }));
          setActiveMultiFilters((active) => ({ ...active, [key]: "" }));
        }
        if (currentPage !== 1) {
          setCurrentPage(1);
        }
      }

      return next;
    });
  };


  const handleFilterInputChange = (key, value) => {
    if (key === "fromDate" || key === "toDate") {
      const nextFrom = key === "fromDate" ? value : filterInputs.fromDate;
      const nextTo = key === "toDate" ? value : filterInputs.toDate;

      if (nextFrom && nextTo && new Date(nextFrom) > new Date(nextTo)) {
        toast.error("From date cannot be greater than to date", {
          autoClose: 2000,
        });
        return;
      }

      setFilterInputs((prev) => ({ ...prev, [key]: value }));
      setActiveMultiFilters((prev) => ({
        ...prev,
        fromDate: nextFrom,
        toDate: nextTo,
      }));
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
      return;
    }

    setFilterInputs((prev) => ({ ...prev, [key]: value }));
    setActiveMultiFilters((prev) => ({ ...prev, [key]: value }));
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };


  const handleClearFilter = () => {
    setFilterChecks(DEFAULT_FILTER_CHECKS);
    setFilterInputs(DEFAULT_FILTER_INPUTS);
    setActiveMultiFilters(DEFAULT_ACTIVE_MULTI_FILTERS);
    setIsFilterPanelOpen(false);
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  const handleClearAll = () => {
    setSearchQuery("");
    setActiveSearchQuery("");
    handleClearFilter();
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value) || 25);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 100);
    }
  };

  const handleSort = (column) => {
    const apiField = SORT_COLUMN_MAP[column] || column;
    if (sortBy === apiField) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(apiField);
      setSortOrder("asc");
    }
    setCurrentPage(1);
  };

  const getSortIcon = (column) => {
    const apiField = SORT_COLUMN_MAP[column] || column;
    if (sortBy !== apiField) {
      return (
        <i
          className="fa-solid fa-sort ml-1 text-muted"
          style={{ fontSize: "0.8em", flexShrink: 0 }}
        />
      );
    }
    return (
      <i
        className={`fa-solid ${
          sortOrder === "asc" ? "fa-sort-up" : "fa-sort-down"
        } ml-1`}
        style={{ fontSize: "0.8em", flexShrink: 0 }}
      />
    );
  };

  const renderSortableHeader = (column, label, extraStyle = {}) => (
    <th
      onClick={() => handleSort(column)}
      style={{
        cursor: "pointer",
        whiteSpace: "nowrap",
        ...extraStyle,
      }}
    >
      <span style={{ display: "inline-flex", alignItems: "center" }}>
        {label}
        {getSortIcon(column)}
      </span>
    </th>
  );

  const isStatCardActive = (card) => {
    if (card.clickable === false) return false;
    if (!card.filterValue || !card.filterType) {
      return !activeMultiFilters.status && !activeMultiFilters.cpoStatus &&
        !activeMultiFilters.goStatus 
    }
    return activeMultiFilters[card.filterType] === card.filterValue;
  };

  const handleStatCardClick = (card) => {
    if (card.clickable === false) return;

    if (!card.filterValue || !card.filterType) {
      setFilterChecks((prev) => ({
        ...prev,
        status: false,
        cpoStatus: false,
        goStatus: false,
      }));
      setFilterInputs((prev) => ({ ...prev, status: "", cpoStatus: "",goStatus: "" }));
      setActiveMultiFilters((prev) => ({
        ...prev,
        status: "",
        cpoStatus: "",
        goStatus: "", 
      }));
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
      return;
    }

    const filterKey = card.filterType;
    const filterLabel =
      filterKey === "cpoStatus"
        ? "CPO Status"
        : filterKey === "goStatus"
          ? "GO Status" 
          : "Status";
    if (!filterChecks[filterKey]) {
      const otherActiveKeys = Object.keys(filterChecks).filter(
        (key) => key !== filterKey && filterChecks[key],
      );

      if (otherActiveKeys.length >= MAX_ACTIVE_FILTERS) {
        const keyToRemove = otherActiveKeys[0];
        const clearRemovedInputs =
          keyToRemove === "dateRange"
            ? { fromDate: "", toDate: "" }
            : { [keyToRemove]: "" };
        setFilterChecks((prev) => ({
          ...prev,
          [keyToRemove]: false,
          [filterKey]: true,
        }));
        setFilterInputs((prev) => ({
          ...prev,
          ...clearRemovedInputs,
          [filterKey]: card.filterValue,
        }));
        setActiveMultiFilters((prev) => ({
          ...prev,
          ...clearRemovedInputs,
          [filterKey]: card.filterValue,
        }));
        toast.info(`Replaced an existing filter to apply ${filterLabel}`, {
          autoClose: 2000,
        });
      } else {
        setFilterChecks((prev) => ({ ...prev, [filterKey]: true }));
        setFilterInputs((prev) => ({
          ...prev,
          [filterKey]: card.filterValue,
        }));
        setActiveMultiFilters((prev) => ({
          ...prev,
          [filterKey]: card.filterValue,
        }));
      }
    } else {
      setFilterInputs((prev) => ({
        ...prev,
        [filterKey]: card.filterValue,
      }));
      setActiveMultiFilters((prev) => ({
        ...prev,
        [filterKey]: card.filterValue,
      }));
    }

    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  const handleDeleteClick = (order) => {
    const oid = getOrderId(order);
    if (!oid) {
      toast.error("Sales order ID is missing", { autoClose: 2000 });
      return;
    }
    setDeleteOrderId(oid);
    setDeleteCpoNumber(cpoNumberValue(order));
  };

  const handleDelete = async () => {
    if (!deleteOrderId) return;
    const currentUserId = getCurrentUserId();
    if (!currentUserId) {
      toast.error("User session expired. Please login again.", {
        autoClose: 2000,
      });
      return;
    }

    setIsDeleting(true);
    try {
      const response = await axios.post(API_URL, {
        action: "delete-sales-order",
        id: deleteOrderId,
        currentUserId,
        requestUserId: currentUserId,
      });
      const responseData = normalizeApiPayload(parseApiResponse(response.data));

      if (isApiSuccess(responseData)) {
        toast.success(
          responseData?.message || "Sales order deleted successfully",
          { autoClose: 2000 },
        );
        if (window.$) {
          window.$("#modaldelete").modal("hide");
        }
        setDeleteOrderId(null);
        setDeleteCpoNumber("");
        if (orders.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        } else {
          fetchOrders();
          fetchOrderStatus();
        }
      } else {
        toast.error(responseData?.message || "Failed to delete sales order", {
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.error("Delete sales order error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      toast.error(
        errData?.message || "An error occurred while deleting sales order",
        { autoClose: 2000 },
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7;
    const tp = totalPages;

    if (tp <= maxVisiblePages) {
      for (let i = 1; i <= tp; i++) pages.push(i);
    } else if (currentPage <= 4) {
      for (let i = 1; i <= 5; i++) pages.push(i);
      pages.push("ellipsis");
      pages.push(tp);
    } else if (currentPage >= tp - 3) {
      pages.push(1);
      pages.push("ellipsis");
      for (let i = tp - 4; i <= tp; i++) pages.push(i);
    } else {
      pages.push(1);
      pages.push("ellipsis");
      for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
      pages.push("ellipsis");
      pages.push(tp);
    }
    return pages;
  };

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1 className="m-0 txt_lft">Sales Orders</h1>
            </div>
            <div className="col-sm-6 txt_rgt">
              {canCreate && (
                <>
                  <Link to="/salesOrderImport" className="btn btn-blue mr-2">
                    Import
                  </Link>
                  <Link to="/salesOrderCreate" className="btn btn-blue">
                    Create Sales Order
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="sales-quote-stat-card-row sales-order-stat-card-row">
                {isStatusLoading
                  ? SALES_ORDER_STAT_CARDS.map((card) => (
                      <SalesOrderStatCardSkeleton key={card.key} />
                    ))
                  : SALES_ORDER_STAT_CARDS.map((card) => {
                      const formatted = formatStatCardValue(
                        card,
                        orderStatus[card.key],
                      );
                      return (
                        <SalesOrderStatCard
                          key={card.key}
                          label={card.label}
                          value={formatted.display}
                          fullValue={formatted.full}
                          titleColor={card.titleColor}
                          valueColor={card.valueColor}
                          isActive={isStatCardActive(card)}
                          clickable={card.clickable !== false}
                          onClick={() => handleStatCardClick(card)}
                        />
                      );
                    })}
              </div>

              <div>
                <div className="card-body">
                  <div className="row align-items-end mb-4 flex-xl-nowrap">
                    <div className="col-12 col-sm-6 col-md-4 col-xl-3 mb-2 mb-xl-0">
                      <div className="form-group mb-0">
                        <label
                          className="mb-1"
                          style={{
                            fontSize: "13px",
                            fontWeight: "500",
                          }}
                        >
                          Search
                        </label>

                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search sales orders..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            onKeyPress={handleSearchKeyPress}
                          />

                          <div className="input-group-append">
                            <div
                              className="input-group-text crsr_pntr"
                              onClick={handleSearchTrigger}
                              style={{ cursor: "pointer" }}
                            >
                              <i className="fa-solid fa-search" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                      <div
                        className="form-group mb-0 position-relative"
                        ref={filterPanelRef}
                      >
                        <label
                          className="mb-1"
                          style={{
                            fontSize: "13px",
                            fontWeight: "500",
                          }}
                        >
                          Filter
                        </label>

                        <button
                          type="button"
                          className="form-control text-left d-flex justify-content-between align-items-center"
                          onClick={() => setIsFilterPanelOpen((open) => !open)}
                        >
                          <span
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {anyMultiFilterActive
                              ? "Filters"
                              : "Select Filters"}
                          </span>

                          <i
                            className={`fa-solid fa-chevron-${
                              isFilterPanelOpen ? "up" : "down"
                            }`}
                            style={{
                              fontSize: "11px",
                              flexShrink: 0,
                              marginLeft: "6px",
                            }}
                          />
                        </button>

                        {isFilterPanelOpen && (
                          <div
                            className="procurement-filter-dropdown"
                            style={{
                              position: "absolute",
                              zIndex: 20,
                              top: "100%",
                              left: 0,
                              marginTop: "4px",
                              background: "#fff",
                              border: "1px solid #ced4da",
                              borderRadius: "4px",
                              padding: "10px 12px",
                              width: "200px",
                              maxWidth: "calc(100vw - 32px)",
                              boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
                            }}
                          >
                            {[
                              {
                                key: "status",
                                id: "filterCheckStatus",
                                label: "Status",
                              },
                              {
                                key: "cpoStatus",
                                id: "filterCheckCpoStatus",
                                label: "CPO Status",
                              },
                              {
                                key: "goStatus",
                                id: "filterCheckGoStatus",
                                label: "GO  Status",
                              },
                              {
                                key: "customer",
                                id: "filterCheckCustomer",
                                label: "Customer",
                              },
                              {
                                key: "createdBy",
                                id: "filterCheckCreatedBy",
                                label: "Created User",
                              },
                              {
                                key: "dateRange",
                                id: "filterCheckDateRange",
                                label: "Date",
                              },
                            ].map((filter) => {
                              const activeCount =
                                Object.values(filterChecks).filter(
                                  Boolean,
                                ).length;

                              return (
                                <div
                                  className="custom-control custom-checkbox mb-2"
                                  key={filter.key}
                                >
                                  <input
                                    type="checkbox"
                                    className="custom-control-input"
                                    id={filter.id}
                                    checked={filterChecks[filter.key]}
                                    disabled={
                                      !filterChecks[filter.key] &&
                                      activeCount >= MAX_ACTIVE_FILTERS
                                    }
                                    onChange={() =>
                                      handleFilterCheckToggle(filter.key)
                                    }
                                  />

                                  <label
                                    className="custom-control-label"
                                    htmlFor={filter.id}
                                  >
                                    {filter.label}
                                  </label>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>

                    {filterChecks.status && (
                      <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                        <div className="form-group mb-0">
                          <label
                            className="mb-1"
                            style={{
                              fontSize: "13px",
                              fontWeight: "500",
                            }}
                          >
                            Status
                          </label>

                          <select
                            className="form-control"
                            value={filterInputs.status}
                            onChange={(e) =>
                              handleFilterInputChange("status", e.target.value)
                            }
                          >
                            <option value="">Select Status</option>

                            {STATUS_FILTER_OPTIONS.map((status) => (
                              <option key={status.value} value={status.value}>
                                {status.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}

                    {filterChecks.cpoStatus && (
                      <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                        <div className="form-group mb-0">
                          <label
                            className="mb-1"
                            style={{
                              fontSize: "13px",
                              fontWeight: "500",
                            }}
                          >
                            CPO Status
                          </label>

                          <select
                            className="form-control"
                            value={filterInputs.cpoStatus}
                            onChange={(e) =>
                              handleFilterInputChange(
                                "cpoStatus",
                                e.target.value,
                              )
                            }
                          >
                            <option value="">Select CPO Status</option>

                            {CPO_STATUS_OPTIONS.map((status) => (
                              <option key={status} value={status}>
                                {status}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}
                   {filterChecks.goStatus && (
                      <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                        <div className="form-group mb-0">
                          <label
                            className="mb-1"
                            style={{
                              fontSize: "13px",
                              fontWeight: "500",
                            }}
                          >
                            GO Status
                          </label>

                          <select
                            className="form-control"
                            value={filterInputs.goStatus}
                            onChange={(e) =>
                              handleFilterInputChange(
                                "goStatus",
                                e.target.value,
                              )
                            }
                          >
                            <option value="">Select GO Status</option>

                            {GO_STATUS_OPTIONS.map((status) => (
                              <option key={status} value={status}>
                                {status}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}

                    {filterChecks.customer && (
                      <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                        <div className="form-group mb-0">
                          <label
                            className="mb-1"
                            style={{
                              fontSize: "13px",
                              fontWeight: "500",
                            }}
                          >
                            Customer
                          </label>

                          <select
                            className="form-control"
                            value={filterInputs.customer}
                            onChange={(e) =>
                              handleFilterInputChange(
                                "customer",
                                e.target.value,
                              )
                            }
                          >
                            <option value="">
                              {isCustomersLoading && customers.length === 0
                                ? "Loading customers..."
                                : "Select Customer"}
                            </option>

                            {customers.map((customer) => (
                              <option key={customer._id} value={customer._id}>
                                {customer.customerName}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}

                    {filterChecks.createdBy && (
                      <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                        <div className="form-group mb-0">
                          <label
                            className="mb-1"
                            style={{
                              fontSize: "13px",
                              fontWeight: "500",
                            }}
                          >
                            Created User
                          </label>

                          <select
                            className="form-control"
                            value={filterInputs.createdBy}
                            onChange={(e) =>
                              handleFilterInputChange(
                                "createdBy",
                                e.target.value,
                              )
                            }
                          >
                            <option value="">
                              {isOrderUsersLoading && orderUsers.length === 0
                                ? "Loading users..."
                                : "Select User"}
                            </option>

                            {orderUsers.map((user) => (
                              <option key={user._id} value={user._id}>
                                {user.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}

                    {filterChecks.dateRange && (
                      <>
                        <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                          <div className="form-group mb-0">
                            <label
                              className="mb-1"
                              style={{ fontSize: "13px", fontWeight: "500" }}
                            >
                              From Date
                            </label>
                            <input
                              type="date"
                              className="form-control"
                              value={filterInputs.fromDate || ""}
                              max={filterInputs.toDate || undefined}
                              onChange={(e) =>
                                handleFilterInputChange(
                                  "fromDate",
                                  e.target.value,
                                )
                              }
                            />
                          </div>
                        </div>
                        <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                          <div className="form-group mb-0">
                            <label
                              className="mb-1"
                              style={{ fontSize: "13px", fontWeight: "500" }}
                            >
                              To Date
                            </label>
                            <input
                              type="date"
                              className="form-control"
                              value={filterInputs.toDate || ""}
                              min={filterInputs.fromDate || undefined}
                              onChange={(e) =>
                                handleFilterInputChange(
                                  "toDate",
                                  e.target.value,
                                )
                              }
                            />
                          </div>
                        </div>
                      </>
                    )}

                    {anyMultiFilterActive && (
                      <div className="col-12 col-sm-6 col-md-4 col-xl-auto mb-2 mb-xl-0">
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm w-100"
                          onClick={handleClearFilter}
                          style={{
                            minWidth: "100px",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <i className="fa-solid fa-times mr-1" />
                          Clear Filter
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {isLoading ? (
                <div className="text-center p-4">
                  <PulseLoader color="#1199B2" size={10} />
                </div>
              ) : orders.length === 0 && !hasActiveFilters ? (
                <div className="nocontent_div text-center p-4">
                  There are no sales orders yet.
                  <br />
                  <div className="mt-3">
                    {canCreate && (
                      <Link to="/salesOrderCreate" className="btn btn-blue">
                        Create Sales Order
                      </Link>
                    )}
                  </div>
                </div>
              ) : orders.length === 0 && hasActiveFilters ? (
                <div className="nocontent_div text-center p-4">
                  No sales orders found matching your filters.
                  <br />
                  <div className="mt-3">
                    <button
                      type="button"
                      className="btn btn-blue"
                      onClick={handleClearAll}
                    >
                      Clear Filters
                    </button>
                  </div>
                </div>
              ) : (
                <div className="supply-table-container">
                  <div className="table-responsive">
                    <table
                      id="salesOrders"
                      width="100%"
                      className="table table-striped table-bordered txt_lft"
                    >
                      <thead>
                        <tr>
                          <th
                            style={{
                              whiteSpace: "nowrap",
                              width: "35px",
                              minWidth: "35px",
                            }}
                          >
                            S.No
                          </th>
                          {renderSortableHeader("cpoNumber", "CPO No", {
                            minWidth: "110px",
                          })}
                          {renderSortableHeader("cpoDate", "CPO Date", {
                            minWidth: "100px",
                          })}
                          {renderSortableHeader(
                            "deliverDate",
                            "Delivery Date",
                            {
                              minWidth: "110px",
                            },
                          )}
                          {renderSortableHeader("customerName", "Customer", {
                            minWidth: "160px",
                          })}
                          <th style={{ minWidth: "170px" }}>Summary</th>
                          {renderSortableHeader("status", "Status", {
                            minWidth: "80px",
                          })}
                          {renderSortableHeader("cpoStatus", "CPO Status", {
                            minWidth: "90px",
                          })}
                            {renderSortableHeader("goStatus", "GO Status", {
                            minWidth: "90px",
                          })}
                          <th style={{ minWidth: "90px" }}>Standalone</th>
                          <th style={{ minWidth: "100px" }}>Gross Total</th>
                          {renderSortableHeader("createdBy", "Created User", {
                            minWidth: "130px",
                          })}
                          {renderSortableHeader("createdAt", "Created Date", {
                            minWidth: "110px",
                          })}
                          <th style={{ width: "80px", minWidth: "80px" }}>
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders.map((order, index) => {
                          const oid = getOrderId(order);
                          return (
                            <tr key={oid || index}>
                              <td>
                                {(currentPage - 1) * itemsPerPage + index + 1}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {cpoNumberValue(order)}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {formatOrderDate(order?.cpoDate)}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {formatOrderDate(order?.deliverDate)}
                              </td>
                              <td
                                style={{
                                  maxWidth: "180px",
                                  wordWrap: "break-word",
                                  whiteSpace: "normal",
                                }}
                              >
                                {customerNameValue(order)}
                              </td>
                              <td
                                style={{
                                  maxWidth: "240px",
                                  wordWrap: "break-word",
                                  whiteSpace: "normal",
                                }}
                              >
                                {summaryValue(order)}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {renderRecordStatus(order?.status)}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {capitalizeStatus(order?.cpoStatus)}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {formatGoStatusLabel(order?.goStatus)}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {renderStandalone(order)}
                              </td>
                              <td
                                style={{
                                  whiteSpace: "nowrap",
                                  fontSize: "0.85rem",
                                }}
                              >
                                {renderGrossTotal(order)}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {createdByName(order)}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {formatOrderDate(order?.createdAt)}
                              </td>
                              <td>
                                <div
                                  align="center"
                                  style={{ whiteSpace: "nowrap" }}
                                  className="d-flex justify-content-center align-items-center"
                                >
                                  {canView && (
                                    <Link
                                      to={`/salesOrderView/${oid}`}
                                      className="crsr_pntr mr-2"
                                      title="View"
                                    >
                                      <i className="fa-solid fa-eye" />
                                    </Link>
                                  )}
                                  {canUpdate && (
                                    <Link
                                      to={`/salesOrderCreate/${oid}`}
                                      className="crsr_pntr mr-2"
                                      title="Edit"
                                    >
                                      <i className="fa-solid fa-pen" />
                                    </Link>
                                  )}
                                  {canDelete && (
                                    <a
                                      href="#modaldelete"
                                      className="crsr_pntr action-icon-link"
                                      data-toggle="modal"
                                      data-target="#modaldelete"
                                      title="Delete"
                                      onClick={() => handleDeleteClick(order)}
                                    >
                                      <i className="fa-solid fa-trash-can" />
                                    </a>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {!isLoading && orders.length > 0 && (
                    <div className="supply-pagination-container">
                      <div className="pagination-wrapper">
                        <div className="pagination-info">
                          <label
                            className="mb-0 mr-2"
                            style={{ fontSize: "13px" }}
                          >
                            Show:
                          </label>
                          <select
                            className="form-control form-control-sm"
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                          >
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                          </select>
                          <span className="ml-2" style={{ fontSize: "13px" }}>
                            entries
                          </span>
                          <span
                            className="ml-3 text-muted"
                            style={{ fontSize: "13px" }}
                          >
                            {startItem}–{endItem} of {totalCount}
                          </span>
                        </div>

                        <nav className="pagination-nav">
                          <ul className="pagination mb-0">
                            <li
                              className={`page-item ${
                                currentPage === 1 ? "disabled" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(1)}
                                disabled={currentPage === 1}
                                title="First Page"
                              >
                                <i className="fa-solid fa-angle-double-left" />
                              </button>
                            </li>
                            <li
                              className={`page-item ${
                                currentPage === 1 ? "disabled" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() =>
                                  handlePageChange(currentPage - 1)
                                }
                                disabled={currentPage === 1}
                                title="Previous Page"
                              >
                                <i className="fa-solid fa-angle-left" />
                              </button>
                            </li>
                            {getPageNumbers().map((page, index) =>
                              page === "ellipsis" ? (
                                <li
                                  key={`ellipsis-${index}`}
                                  className="page-item disabled"
                                >
                                  <span className="page-link">...</span>
                                </li>
                              ) : (
                                <li
                                  key={page}
                                  className={`page-item ${
                                    currentPage === page ? "active" : ""
                                  }`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => handlePageChange(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              ),
                            )}
                            <li
                              className={`page-item ${
                                currentPage === totalPages ? "disabled" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() =>
                                  handlePageChange(currentPage + 1)
                                }
                                disabled={currentPage === totalPages}
                                title="Next Page"
                              >
                                <i className="fa-solid fa-angle-right" />
                              </button>
                            </li>
                            <li
                              className={`page-item ${
                                currentPage === totalPages ? "disabled" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(totalPages)}
                                disabled={currentPage === totalPages}
                                title="Last Page"
                              >
                                <i className="fa-solid fa-angle-double-right" />
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="modal fade" id="modaldelete">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Delete Sales Order</h4>
              <button type="button" className="close" data-dismiss="modal">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div className="modal-body">
              <p>
                Are you sure you want to delete sales order{" "}
                <strong>{deleteCpoNumber || "-"}</strong>?
              </p>
            </div>
            <div className="modal-footer justify-content-between">
              <button
                type="button"
                className="btn btn-default"
                data-dismiss="modal"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesOrder;